import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '../utils/toast';
import { Plus, Search, Eye, Edit, XCircle, BookOpen, CheckCircle, FileText, UserPlus, Settings, MoreVertical } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrainingContentPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('default'); // 'default', 'own', or 'settings' for Hospital Admin

  // Training Settings state
  const [trainingMode, setTrainingMode] = useState('adaptive');
  const [assessmentMode, setAssessmentMode] = useState('chapter_wise');
  const [passingPercentage, setPassingPercentage] = useState(80);
  const [trainingModeLoading, setTrainingModeLoading] = useState(false);
  const [trainingModeSaving, setTrainingModeSaving] = useState(false);
  
  // Assignment Dialog State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningTopic, setAssigningTopic] = useState(null);
  const [assignmentType, setAssignmentType] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [staffSearchText, setStaffSearchText] = useState(''); // Separate state for search input
  const [assignLoading, setAssignLoading] = useState(false);

  // Derive total languages configured for a training chapter item
  const getLanguageCount = (content) => {
    const langs = new Set();
    // Collect from title translations (includes English and others)
    if (content?.title_translations) {
      Object.keys(content.title_translations).forEach((l) => l && langs.add(l));
    }
    // Standalone language versions
    if (Array.isArray(content?.language_versions)) {
      content.language_versions.forEach((v) => v?.language && langs.add(v.language));
    }
    // Subchapter-based content: gather from each section
    if (Array.isArray(content?.sections)) {
      content.sections.forEach((sec) => {
        if (sec?.section_title_translations) {
          Object.keys(sec.section_title_translations).forEach((l) => l && langs.add(l));
        }
        if (Array.isArray(sec?.language_versions)) {
          sec.language_versions.forEach((v) => v?.language && langs.add(v.language));
        }
      });
    }
    // Fallback: if nothing detected but there is a base title, assume English
    if (langs.size === 0 && content?.title) langs.add('English');
    return langs.size;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access Training Chapters');
      navigate('/signin');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Check if user has training access
    const trainingRoles = ['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'ExlvaAdminLite', 'Hospital Admin'];
    if (!trainingRoles.includes(parsedUser.role)) {
      toast.error('You don\'t have access to Training Chapters Management');
      navigate('/dashboard');
      return;
    }

    // Check license status for Hospital Admin
    const checkLicenseAndFetch = async () => {
      if (parsedUser.role === 'Hospital Admin') {
        try {
          const response = await axios.get(`${API}/subscriptions/check-license`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!response.data.is_licensed) {
            toast.error('Training Chapters access requires an active subscription. Please subscribe to a plan.');
            navigate('/subscription');
            return;
          }
        } catch (error) {
          console.error('Failed to check license status:', error);
          toast.error('Unable to verify subscription status.');
          navigate('/subscription');
          return;
        }
      }

      fetchContent();
      if (parsedUser.role === 'Hospital Admin') fetchTrainingSettings();
    };

    checkLicenseAndFetch();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process content to generate signed URLs for S3 thumbnails
      const processedContent = await Promise.all(response.data.map(async (content) => {
        // Check if thumbnail_url exists and is an S3 key (doesn't start with http)
        if (content.thumbnail_url && !content.thumbnail_url.startsWith('http')) {
          try {
            const signedResponse = await axios.post(
              `${API}/s3/signed-url`,
              { s3_key: content.thumbnail_url, expiration: 3600 },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            return {
              ...content,
              thumbnail_signed_url: signedResponse.data.signed_url
            };
          } catch (error) {
            console.warn('Failed to generate signed URL for thumbnail:', error);
            return content;
          }
        }
        return content;
      }));
      
      setContentList(processedContent);
    } catch (error) {
      toast.error('Failed to fetch training chapters');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingSettings = async () => {
    setTrainingModeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital/training-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingMode(response.data.training_mode || 'adaptive');
      setAssessmentMode(response.data.assessment_mode || 'chapter_wise');
      setPassingPercentage(response.data.passing_percentage || 80);
    } catch (error) {
      console.error('Failed to fetch training settings:', error);
    } finally {
      setTrainingModeLoading(false);
    }
  };

  // Save one or more training-settings fields. Backend merges partial updates.
  const saveTrainingSettings = async (updates, successMessage) => {
    setTrainingModeSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/hospital/training-settings`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (updates.training_mode !== undefined) setTrainingMode(updates.training_mode);
      if (updates.assessment_mode !== undefined) setAssessmentMode(updates.assessment_mode);
      if (updates.passing_percentage !== undefined) setPassingPercentage(updates.passing_percentage);
      toast.success(successMessage || 'Training settings updated');
    } catch (error) {
      toast.error('Failed to update training settings');
    } finally {
      setTrainingModeSaving(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this training chapter?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training chapter deleted successfully');
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete content');
    }
  };

  // Open Assignment Dialog
  const openAssignDialog = async (topic) => {
    setAssigningTopic(topic);
    setAssignmentType('all');
    setSelectedDept('');
    setSelectedJob('');
    setSelectedStaff('');
    setStaffSearchText('');
    setAssignDialogOpen(true);
    
    // Fetch departments, job roles (default + hospital), and employees
    try {
      const token = localStorage.getItem('token');
      const [deptRes, jobRes, empRes] = await Promise.all([
        axios.get(`${API}/hospital-config/departments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital-config/job-functions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital/employees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDepartments(deptRes.data.departments || []);
      setJobFunctions(jobRes.data.job_functions || []);
      setEmployees(empRes.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch assignment options:', error);
    }
  };

  // Handle Chapter Assignment
  const handleAssignTopic = async () => {
    if (!assigningTopic) return;
    
    if (assignmentType === 'department' && !selectedDept) {
      toast.error('Please select a department');
      return;
    }
    
    if (assignmentType === 'job_function' && !selectedJob) {
      toast.error('Please select a job role');
      return;
    }
    
    if (assignmentType === 'individual' && !selectedStaff) {
      toast.error('Please select a staff member');
      return;
    }
    
    setAssignLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // For individual assignment, we'll use a different approach
      if (assignmentType === 'individual') {
        // Assign to individual by creating a progress record directly
        await axios.post(
          `${API}/hospital/topics/assign-individual`,
          {
            training_topic_id: assigningTopic.id,
            employee_id: selectedStaff
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/hospital/topics/assign`,
          {
            training_topic_ids: [assigningTopic.id],
            assignment_type: assignmentType,
            department_id: assignmentType === 'department' ? selectedDept : null,
            job_function_id: assignmentType === 'job_function' ? selectedJob : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Chapter "${assigningTopic.title}" assigned successfully`);
      setAssignDialogOpen(false);
      setAssigningTopic(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign chapter');
    } finally {
      setAssignLoading(false);
    }
  };

  // Filter content based on active tab (for Hospital Admin)
  const getFilteredContentByTab = () => {
    if (user?.role !== 'Hospital Admin') {
      // For Super Admin and Exlva Admin, show all content
      return contentList;
    }
    
    if (activeTab === 'default') {
      // Default Training Chapters: Show ONLY Super Admin and Exlva Admin created content
      return contentList.filter(content => 
        content.created_by_role === 'Super Admin' || 
        content.created_by_role === 'Exlva Admin'
      );
    } else {
      // Hospital Own Training Chapters: Show ONLY Hospital Admin's own content
      return contentList.filter(content => content.created_by_id === user.id);
    }
  };

  const tabFilteredContent = getFilteredContentByTab();
  
  const filteredContent = tabFilteredContent.filter(content =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || !user) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading training chapters...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">TRAINING CHAPTERS</h1>
          {user.role === 'Hospital Admin' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">Training Chapters Overview</h3>
                  <p className="text-sm text-blue-800">
                    Use tabs to switch between <strong>Default Training Chapters</strong> (global chapters - read-only) and <strong>Institution Own Training Chapters</strong> (your chapters - full access). Assign both types to Job Roles.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Cards - Matching Dashboard Style */}
        {activeTab !== 'settings' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">Total Chapters</p>
                <h3 className="text-4xl font-bold text-blue-900">{tabFilteredContent.length}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-green-700 mb-2">Published Chapters</p>
                <h3 className="text-4xl font-bold text-green-900">{tabFilteredContent.filter(c => c.status === 'published').length}</h3>
              </div>
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-orange-700 mb-2">Chapters in Draft</p>
                <h3 className="text-4xl font-bold text-orange-900">{tabFilteredContent.filter(c => c.status === 'draft').length}</h3>
              </div>
              <div className="w-12 h-12 bg-orange-200 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-700" />
              </div>
            </div>
          </div>
        </div>}

        <div className="bg-white rounded-2xl p-6 shadow-xl card-shadow">
          {/* Tab Navigation for Hospital Admin */}
          {user.role === 'Hospital Admin' && (
            <div className="mb-6 border-b border-gray-200">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('default')}
                  className={`pb-4 px-2 font-semibold border-b-2 transition-colors ${
                    activeTab === 'default'
                      ? 'border-[#0056B3] text-[#0056B3]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Default Training Chapters
                </button>
                <button
                  onClick={() => setActiveTab('own')}
                  className={`pb-4 px-2 font-semibold border-b-2 transition-colors ${
                    activeTab === 'own'
                      ? 'border-[#0056B3] text-[#0056B3]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Institution Own Training Chapters
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`pb-4 px-2 font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'settings'
                      ? 'border-[#0056B3] text-[#0056B3]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Training Settings
                </button>
              </div>
            </div>
          )}

          {/* Training Settings Tab Content */}
          {activeTab === 'settings' && user.role === 'Hospital Admin' && (
            <div className="py-4">
              <h2 className="text-[22px] font-semibold mb-1" style={{ color: '#0056B3' }}>Training Settings</h2>
              <p className="text-sm text-gray-600 mb-6">Configure the training mode for all staff in your institution</p>

              {trainingModeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="max-w-2xl">
                  <div className="mb-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Training Mode</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose how your staff will progress through training chapters. This setting applies to all staff members in your institution.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Adaptive option */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        trainingMode === 'adaptive'
                          ? 'border-[#0056B3] bg-blue-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trainingMode"
                        value="adaptive"
                        checked={trainingMode === 'adaptive'}
                        onChange={() => saveTrainingSettings({ training_mode: 'adaptive' }, 'Training mode updated to Adaptive')}
                        disabled={trainingModeSaving}
                        className="mt-1 w-4 h-4 text-[#0056B3] focus:ring-[#0056B3]"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">Adaptive Learning</span>
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Recommended</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Staff progress through training with adaptive assessments. Topics are unlocked sequentially based on performance.
                          If a staff member does not pass the adaptive assessment for a subchapter, they are required to watch the full
                          training content before proceeding, and must also complete the chapter-level assessment.
                        </p>
                      </div>
                    </label>

                    {/* Conventional option */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        trainingMode === 'conventional'
                          ? 'border-[#0056B3] bg-blue-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trainingMode"
                        value="conventional"
                        checked={trainingMode === 'conventional'}
                        onChange={() => saveTrainingSettings({ training_mode: 'conventional' }, 'Training mode updated to Conventional')}
                        disabled={trainingModeSaving}
                        className="mt-1 w-4 h-4 text-[#0056B3] focus:ring-[#0056B3]"
                      />
                      <div>
                        <span className="font-semibold text-gray-900">Conventional Learning</span>
                        <p className="text-sm text-gray-600 mt-1">
                          Staff must watch all training content for each subchapter and complete the chapter-level assessment
                          to finish a topic. No adaptive shortcuts — every staff member follows the same linear path.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Assessment Mode (applies to Conventional Learning) */}
                  <div className="mt-8 mb-3">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Assessment Mode</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose how assessments are delivered within a chapter. This applies to <strong>Conventional Learning</strong>.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {/* Chapter-wise */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        assessmentMode === 'chapter_wise'
                          ? 'border-[#0056B3] bg-blue-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="assessmentMode"
                        value="chapter_wise"
                        checked={assessmentMode === 'chapter_wise'}
                        onChange={() => saveTrainingSettings({ assessment_mode: 'chapter_wise' }, 'Assessment mode updated to Chapter-wise')}
                        disabled={trainingModeSaving}
                        className="mt-1 w-4 h-4 text-[#0056B3] focus:ring-[#0056B3]"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">Chapter-wise Assessment</span>
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Default</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Staff complete all sub-chapters, then take one final chapter assessment. They must pass it to complete the chapter.
                        </p>
                      </div>
                    </label>

                    {/* Sub-chapter-wise */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        assessmentMode === 'subchapter_wise'
                          ? 'border-[#0056B3] bg-blue-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="assessmentMode"
                        value="subchapter_wise"
                        checked={assessmentMode === 'subchapter_wise'}
                        onChange={() => saveTrainingSettings({ assessment_mode: 'subchapter_wise' }, 'Assessment mode updated to Sub-chapter-wise')}
                        disabled={trainingModeSaving}
                        className="mt-1 w-4 h-4 text-[#0056B3] focus:ring-[#0056B3]"
                      />
                      <div>
                        <span className="font-semibold text-gray-900">Sub-chapter-wise Assessment</span>
                        <p className="text-sm text-gray-600 mt-1">
                          Every sub-chapter has a <strong>pre-assessment</strong> (score recorded for analytics only, no passing criteria) before the
                          content, and a <strong>post-assessment</strong> after it. Staff must pass the post-assessment to complete the sub-chapter
                          and unlock the next one.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Passing Percentage */}
                  <div className="mt-8">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Passing Percentage</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Minimum score required to pass post-assessments for your institution. Default is 80%.
                    </p>
                    <select
                      value={passingPercentage}
                      onChange={(e) => saveTrainingSettings({ passing_percentage: parseInt(e.target.value, 10) }, `Passing percentage updated to ${e.target.value}%`)}
                      disabled={trainingModeSaving}
                      className="w-40 rounded-lg border-2 border-gray-200 px-3 py-2 text-sm bg-white focus:border-[#0056B3] focus:outline-none"
                    >
                      {[50, 60, 70, 80, 90].map((p) => (
                        <option key={p} value={p}>{p}%</option>
                      ))}
                    </select>
                  </div>

                  {trainingModeSaving && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Saving...
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Changing the training mode takes effect immediately for all staff.
                      Staff who are mid-training will continue under the new mode from their current progress point.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content List (hidden when Training Settings tab is active) */}
          {activeTab !== 'settings' && <>
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>
                {user.role === 'Hospital Admin'
                  ? (activeTab === 'default' ? 'Default Training Chapters' : 'Institution Own Training Chapters')
                  : 'Training Chapter Library'
                }
              </h2>
              {user.role === 'Hospital Admin' && activeTab === 'default' && (
                <p className="text-sm text-gray-600 mt-1">
                  View-only access to chapters created by Super Admin and Eptral Admin
                </p>
              )}
              {user.role === 'Hospital Admin' && activeTab === 'own' && (
                <p className="text-sm text-gray-600 mt-1">
                  Create, edit, and manage your institution-specific training chapters
                </p>
              )}
            </div>
            {/* Show Add Chapter button only for non-Hospital Admin OR Hospital Admin on "own" tab */}
            {(user.role !== 'Hospital Admin' || activeTab === 'own') && (
              <Button
                data-testid="add-content-btn"
                onClick={() => navigate('/training/add')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Chapter
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-input"
                placeholder="Search training chapters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Content Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Thumbnail</TableHead>
                  <TableHead className="w-1/4">Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Subchapters</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContent.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No training chapters found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContent.map((content) => (
                    <TableRow key={content.id}>
                      <TableCell>
                        {content.thumbnail_signed_url || content.thumbnail_url ? (
                          <img 
                            src={content.thumbnail_signed_url || content.thumbnail_url} 
                            alt={content.title}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{content.title}</TableCell>
                      <TableCell>
                        <Badge className={
                          content.training_type === 'offline' ? 'bg-amber-100 text-amber-800' :
                          content.training_type === 'hybrid' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          {content.training_type === 'offline' ? 'Offline' :
                           content.training_type === 'hybrid' ? 'Hybrid' : 'Online'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {content.sections?.length || 0}
                      </TableCell>
                      <TableCell>
                        {getLanguageCount(content)} language(s)
                      </TableCell>
                      <TableCell>
                        <Badge className={content.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {content.status === 'published' ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell>{content.created_by_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1 min-w-[88px]">
                          {/* Assign button for Hospital Admin only */}
                          {user.role === 'Hospital Admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAssignDialog(content)}
                              data-tooltip="Assign Chapter"
                              aria-label="Assign Chapter"
                              className="h-9 w-9 shrink-0 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Edit/Delete only for own content or if Super Admin/Exlva Admin/ExlvaAdmin/ExlvaAdminLite */}
                          {(content.created_by_id === user.id || ['Super Admin', 'Exlva Admin', 'Admin', 'ExlvaAdmin', 'ExlvaAdminLite'].includes(user.role)) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-tooltip="More actions"
                                  aria-label="More chapter actions"
                                  className="h-9 w-9 shrink-0 p-0"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" sideOffset={6} className="w-48">
                                <DropdownMenuItem onClick={() => navigate(`/training/${content.id}/edit`)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Content
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(content.id)}
                                  className="text-red-600 focus:text-red-700"
                                >
                                  <XCircle className="mr-2 h-4 w-4" />
                                  Delete Content
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          </>}
        </div>

        {/* Assignment Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-[#0056B3]">Assign Training Chapter</DialogTitle>
              <DialogDescription>
                Assign "<span className="font-medium">{assigningTopic?.title}</span>" to staff members
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Assignment Type Selection */}
              <div className="space-y-2">
                <Label>Assignment Type *</Label>
                <Select value={assignmentType} onValueChange={setAssignmentType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff (Institution-wide)</SelectItem>
                    <SelectItem value="department">By Department</SelectItem>
                    <SelectItem value="job_function">By Job Role</SelectItem>
                    <SelectItem value="individual">Individual Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Department Selection */}
              {assignmentType === 'department' && (
                <div className="space-y-2">
                  <Label>Select Department *</Label>
                  <Select value={selectedDept} onValueChange={setSelectedDept}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose department" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortObjectOptions(departments, 'department_name').map(d => (
                        <SelectItem key={d.department_id} value={d.department_id}>
                          {d.department_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Job Role Selection */}
              {assignmentType === 'job_function' && (
                <div className="space-y-2">
                  <Label>Select Job Role *</Label>
                  <Select value={selectedJob} onValueChange={setSelectedJob}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose job role" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortObjectOptions(jobFunctions, 'job_function_name').map(j => (
                        <SelectItem key={j.job_function_id} value={j.job_function_id}>
                          {j.job_function_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Individual Staff Selection with Autocomplete */}
              {assignmentType === 'individual' && (
                <div className="space-y-2">
                  <Label>Enter Staff Name *</Label>
                  <Input
                    placeholder="Type staff name..."
                    value={staffSearchText}
                    onChange={e => {
                      setStaffSearchText(e.target.value);
                      setSelectedStaff(''); // Clear selection when user types
                    }}
                    autoComplete="off"
                  />
                  {/* Autocomplete suggestions */}
                  {staffSearchText.length >= 3 && (
                    <div className="border rounded bg-white shadow max-h-40 overflow-y-auto mt-1">
                      {employees.filter(e => {
                        // Filter out employees without user accounts
                        if (!e.user_id) return false;
                        
                        const searchLower = staffSearchText.toLowerCase().trim();
                        const employeeNameLower = (e.full_name || e.name || '').toLowerCase().trim();
                        const firstNameLower = (e.first_name || '').toLowerCase().trim();
                        const lastNameLower = (e.last_name || '').toLowerCase().trim();
                        const fullNameLower = employeeNameLower || `${firstNameLower} ${lastNameLower}`.trim();
                        
                        // Match if first name, last name, or full name starts with search term
                        return fullNameLower.startsWith(searchLower) ||
                               firstNameLower.startsWith(searchLower) || 
                               lastNameLower.startsWith(searchLower) ||
                               fullNameLower.includes(searchLower);
                      }).map(emp => (
                        <div
                          key={emp.employee_id}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                          onClick={() => {
                            setSelectedStaff(emp.employee_id);
                            setStaffSearchText(emp.full_name || emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim());
                          }}
                        >
                          {emp.full_name || emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()} - {emp.job_function_name || 'No Job Role'}
                        </div>
                      ))}
                      {employees.filter(e => {
                        if (!e.user_id) return false;
                        const searchLower = staffSearchText.toLowerCase().trim();
                        const employeeNameLower = (e.full_name || e.name || '').toLowerCase().trim();
                        const firstNameLower = (e.first_name || '').toLowerCase().trim();
                        const lastNameLower = (e.last_name || '').toLowerCase().trim();
                        const fullNameLower = employeeNameLower || `${firstNameLower} ${lastNameLower}`.trim();
                        return fullNameLower.startsWith(searchLower) ||
                               firstNameLower.startsWith(searchLower) || 
                               lastNameLower.startsWith(searchLower) ||
                               fullNameLower.includes(searchLower);
                      }).length === 0 && (
                        <div className="px-3 py-2 text-gray-400">
                          No staff found
                        </div>
                      )}
                    </div>
                  )}
                  {employees.filter(e => e.user_id).length === 0 && staffSearchText.length < 3 && (
                    <p className="text-sm text-amber-600">No staff with active accounts found. Create staff accounts first.</p>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setAssignDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignTopic} 
                className="bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={assignLoading}
              >
                {assignLoading ? 'Assigning...' : 'Assign Chapter'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TrainingContentPage;
